<template>
	<contact-info-modal
		ref="$clientInfoModal"
		:title="$t('contact_info_title')"
	/>
	<div
		:class="['client-item', { 'client-item--touch': isMobile || isTablet }]"
		:data-contact-id="props.client.Id"
	>
		<app-loader
			v-if="isEditedClient"
			:style="loaderStyle"
		/>
		<div class="client-item__header">
			<div class="client-item__header-main">
				<app-tooltip
					v-if="ctx.context === 'project'"
					class="client-item__header-checkbox"
					:text="mainInProjectTooltipText"
				>
					<template #activator>
						<app-checkbox
							:value="props.client.MainInProject"
							@on-input="onMainInProjectChange"
						/>
					</template>
				</app-tooltip>
				<div
					class="client-item__header-name"
					v-html="highlightedName"
				/>
			</div>
			<div class="client-item__header-address">{{ clientAddress }}</div>
		</div>
		<div class="client-item__subheader">
			<template
				v-for="el in postSegments"
				:key="el.text"
			>
				<div
					v-if="el.isUrl"
					class="client-item__subheader-link"
					v-html="el.text"
					@click="openExternalLink(el.text)"
				/>
				<div
					v-else
					class="client-item__subheader-text"
					v-html="el.text"
				/>
			</template>
		</div>
		<div class="client-item__organizations">
			<app-resizable-dropdown
				v-if="!isContactsReadonly"
				position="left"
				resizable
				:initial-width="400"
				:initial-height="450"
				:min-width="400"
				:max-width="800"
				:min-height="450"
				:max-height="900"
				:mobile-title="$t('choose_clients')"
				ref="$addClientToContact"
				name="$addClientToContact"
			>
				<template #activator>
					<app-tooltip :text="$t('contact_add_client')">
						<template #activator>
							<app-icon
								class="client-item__organizations-add-btn"
								name="plus"
								width="14px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<contact-add-client-dropdown
					:contact-id="props.client.Id"
					:client="props.client"
					:added-clients="props.client.Clients"
					show-close-icon
					@close="closeAddClientDropdown"
				/>
			</app-resizable-dropdown>
			<span
				v-if="!props.client.Clients.length && !isContactsReadonly"
				class="client-item__organizations-add-clients"
			>
				{{ $t('contact_add_client') }}
			</span>
			<template
				v-for="client in props.client.Clients"
				:key="client.Id"
			>
				<span class="client-item__organizations-list-item">
					<router-link
						class="client-item__organizations-list-item-link"
						:to="{ query: { page: 'client', id: client.Id } }"
					>
						<span v-html="highlightSearchText(props.searchText, client.Name)" />
					</router-link><template v-if="props.client.Clients.length > 1 && !isContactsReadonly">&nbsp;<app-resizable-dropdown
						:ref="el => setDetachDropdown(client.Id, el)"
						position="left"
						centered-on-mobile
						:close-by-click-outside="true"
					>
						<template #activator>
							<app-tooltip :text="$t('contact_detach')">
								<template #activator>
									<app-icon
										class="client-item__organizations-list-item-delete"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<confirm-action-dropdown
							:description="$t('contact_detach_question')"
							@cancel="closeDetachDropdown(client.Id)"
							@confirm="onConfirmDetachClient(client.Id)"
						/>
					</app-resizable-dropdown></template>
				</span>&nbsp;
			</template>
		</div>
		<div class="client-item__clients">
			<div
				v-for="(segments, index) in contactsSegments"
				class="client-item__clients-item"
				:key="index"
			>
				<template
					v-for="el in segments"
					:key="el"
				>
					<app-resizable-dropdown
						v-if="el.isPhone"
						position="bottom"
						trigger="hover"
						:initial-width="125"
						:initial-height="62"
						:fullscreen-on-mobile="false"
						:mobile-title="$t('contact_choose_action')"
					>
						<template #activator>
							<div class="client-item__clients-item-phone-field">
								<div
									class="client-item__clients-item-phone"
									v-html="el.text"
									@click.stop="callPhone(el.text)"
								/>
								<app-icon
									class="
										client-item__clients-item-phone-icon
										client-item__clients-item-phone-icon--phone
									"
									name="phone" />
								<app-icon
									class="
										client-item__clients-item-phone-icon
										client-item__clients-item-phone-icon--called-phone
									"
									name="phone-called"
								/>
							</div>
						</template>
						<contacts-email-phone-options-dropdown
							type="phone"
							@on-call="callPhone(el.text)"
							@on-copy="onCopyPhoneAndEmail(el.text)"
						/>
					</app-resizable-dropdown>
					<app-resizable-dropdown
						v-else-if="el.isEmail"
						position="bottom"
						trigger="hover"
						:initial-width="125"
						:initial-height="62"
						:fullscreen-on-mobile="false"
						:mobile-title="$t('contact_choose_action')"
					>
						<template #activator>
							<div class="client-item__clients-item-email-field">
								<a
									class="client-item__clients-item-email"
									v-html="el.text"
									@click.stop.prevent="onEmail(el.text)"
								/>
								<app-icon
									class="client-item__clients-item-email-icon"
									name="envelope"
									color="#979797"
								/>
							</div>
						</template>
						<contacts-email-phone-options-dropdown
							type="email"
							@on-email="onEmail(el.text)"
							@on-copy="onCopyPhoneAndEmail(el.text)"
						/>
					</app-resizable-dropdown>
					<div
						v-else-if="el.isUrl"
						class="client-item__clients-item-email"
						v-html="el.text"
						@click.prevent.stop="openExternalLink(el.text)"
					/>
					<div
						v-else
						class="client-item__clients-item-text"
						v-html="highlightSearchText(props.searchText, el.text)"
					/>
				</template>
			</div>
		</div>
		<div class="client-item__footer">
			<div class="client-item__footer-tags">
				<app-resizable-dropdown
					v-if="!isContactsReadonly"
					ref="$addTag"
					position="left"
					:close-by-click-outside="true"
					:max-width="640"
					:max-height="460"
					:mobile-title="$t('choose_tags')"
					name="$addTagContactItem"
				>
					<template #activator>
						<app-tooltip :text="$t('file_add_tag')">
							<template #activator>
								<app-icon
									class="client-item__footer-tags-tag"
									name="hash"
									width="16px"
									color="#979797"
									cursor="pointer"
									hover-color="#3F884F"
								/>
							</template>
						</app-tooltip>
					</template>
					<app-tag-selector
						:model-value="contactTagsModel"
						:entity-type="TagEntityType.Person"
						mode="select"
						allow-create
						@update:model-value="onSelectorChange"
						@create-tag="onCreateTag"
						@close="$addTag?.close()"
					/>
				</app-resizable-dropdown>
				<app-icon
					v-else
					class="client-item__footer-tags-tag"
					name="hash"
					width="16px"
					color="#D2D2D2"
				/>
				<template
					v-for="tag in props.client.Tags"
					:key="tag.Id"
				>
					<span class="client-item__footer-tags-item">
						<span
							class="client-item__footer-tags-item-name"
							v-html="highlightSearchText(props.searchText, tag.Name.toLowerCase())"
						/>
						<app-tooltip
							v-if="!isContactsReadonly"
							:text="$t('ui_tag_detach')"
						>
							<template #activator>
								<app-icon
									class="client-item__footer-tags-item-delete"
									name="close-bold"
									width="12px"
									color="#D2D2D2"
									hover-color="#BA4C4C"
									cursor="pointer"
									@click.stop="onDetachTag(tag.Id)"
								/>
							</template>
						</app-tooltip>
					</span>
				</template>
			</div>
			<div class="client-item__footer-options">
				<app-tooltip
					v-if="!isMobile && hasAttachedClients"
					ref="$shareTooltip"
					:text="shareTooltipText"
				>
					<template #activator>
						<app-icon
							class="client-item__footer-options-btn"
							:style="{ marginTop: '1px' }"
							name="share"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="onCopyClientRoute"
						/>
					</template>
				</app-tooltip>
				<app-tooltip v-if="!isMobile" :text="$t('information')">
					<template #activator>
						<app-icon
							class="client-item__footer-options-btn"
							name="info-circle-outline"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="$clientInfoModal.onOpen(props.client.Id)"
						/>
					</template>
				</app-tooltip>
				<app-resizable-dropdown
					v-if="!isContactsReadonly"
					position="left"
					resizable
					:initial-width="320"
					:initial-height="398"
					:min-width="320"
					:max-width="640"
					:min-height="398"
					:max-height="800"
					:close-by-click-outside="false"
					:hide-by-parent-scroll="false"
					:mobile-title="$t('contact_edit_title')"
					ref="$createEditContactModal"
					name="$createEditContactModal"
				>
					<template #activator>
						<app-tooltip v-if="!isMobile" :text="$t('edit_label')">
							<template #activator>
								<app-icon
									class="client-item__footer-options-btn"
									name="edit-square-outline"
									width="18px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<create-edit-contact-dropdown
						:client="props.client"
						type="edit"
						@on-close="$createEditContactModal.close()"
					/>
				</app-resizable-dropdown>
				<app-resizable-dropdown
					v-if="!isContactsReadonly"
					position="left"
					centered-on-mobile
					:close-by-click-outside="true"
					ref="$deleteContactDropdown"
					name="$deleteContactDropdown"
				>
					<template #activator>
						<app-tooltip v-if="!isMobile" :text="$t('remove')">
							<template #activator>
								<app-icon
									class="client-item__footer-options-btn"
									name="delete-outline"
									width="18px"
									color="#979797"
									hover-color="#BA4C4C"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<confirm-action-dropdown
						:description="$t('contact_delete_question', { name: props.client.Name })"
						@cancel="$deleteContactDropdown?.close()"
						@confirm="onConfirmDelete"
					/>
				</app-resizable-dropdown>
				<app-resizable-dropdown
					v-if="isMobile"
					ref="$contactActionsDropdown"
					position="right"
					:mobile-title="$t('contact_choose_action')"
				>
					<template #activator>
						<app-icon
							class="client-item__footer-options-btn client-item__footer-options-menu"
							name="options-menu"
							width="18px"
							color="#979797"
							cursor="pointer"
						/>
					</template>
					<div class="client-item__actions-menu">
						<div
							v-if="hasAttachedClients"
							class="client-item__actions-menu-item"
							@click="shareContactFromMenu"
						>
							<app-icon name="share" width="18px" color="#979797" />
							<span>{{ $t('ui_share') }}</span>
						</div>
						<div
							class="client-item__actions-menu-item"
							@click="openContactInfoFromMenu"
						>
							<app-icon name="info-circle-outline" width="18px" color="#979797" />
							<span>{{ $t('information') }}</span>
						</div>
						<div
							v-if="!isContactsReadonly"
							class="client-item__actions-menu-item"
							@click="openContactEditFromMenu"
						>
							<app-icon name="edit-square-outline" width="18px" color="#979797" />
							<span>{{ $t('edit_label') }}</span>
						</div>
						<div
							v-if="!isContactsReadonly"
							class="client-item__actions-menu-item"
							@click="openContactDeleteFromMenu"
						>
							<app-icon name="delete-outline" width="18px" color="#979797" />
							<span>{{ $t('remove') }}</span>
						</div>
					</div>
				</app-resizable-dropdown>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeUnmount,
	ref
} from "vue";
import {
	Client,
	clientsStore
} from "@/store/clients";
import ContactInfoModal from "@/components/main/clients-panel/modals/ContactInfoModal.vue";
import {splitTextByPhoneNumbers} from "@/utils/contactsHelpers";
import { i18n } from "@/i18n";
import {PersonApi} from "@/ api/PersonApi";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import { highlightSearchText } from "@/utils/textHelpers";
import AppLoader from "@/components/common/AppLoader.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import ContactAddClientDropdown from "@/components/main/clients-panel/dropdowns/ContactAddClientDropdown.vue";
import CreateEditContactDropdown from "@/components/main/clients-panel/dropdowns/CreateEditContactDropdown.vue";
import ConfirmActionDropdown from "@/components/main/clients-panel/dropdowns/ConfirmActionDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import ContactsEmailPhoneOptionsDropdown
	from "@/components/main/clients-panel/dropdowns/ContactsEmailPhoneOptionsDropdown.vue";
import {buildAbsoluteClientUrl, normalizeUrlForOpening} from "@/utils/urlHelpers";
import {copyTextToClipboard} from "@/utils/clipboard";
import {useBreakpoints} from "@/composables/useBreakpoints";
import {useContactsContext} from "@/composables/useContactsContext";
import {useContactsAccess} from "@/composables/useContactsAccess";

const props = defineProps<{
	client: Client,
	searchText: string
}>()

const store = clientsStore()
const ctx = useContactsContext()
const { isContactsReadonly } = useContactsAccess()
const { isMobile, isTablet } = useBreakpoints()

const $addClientToContact = ref<typeof AppResizableDropdown | null>(null)
const $clientInfoModal = ref<InstanceType<typeof ContactInfoModal> | null>(null)
const $createEditContactModal = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $deleteContactDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $addTag = ref<typeof AppResizableDropdown | null>(null)
const $shareTooltip = ref<typeof AppTooltip | null>(null)
const $contactActionsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)

const SHARE_TOOLTIP_KEY = 'ui_share'
const SHARE_COPIED_KEY = 'link_copied_to_clipboard'
const SHARE_COPIED_DELAY = 3000

const shareTooltipKey = ref(SHARE_TOOLTIP_KEY)
const shareTooltipText = computed(() => String(i18n.global.t(shareTooltipKey.value)))
const hasAttachedClients = computed(() => !!props.client.Clients?.length)
const mainInProjectTooltipText = computed(() => String(i18n.global.t(
	props.client.MainInProject ? 'contact_detach_from_project' : 'contact_attach_to_project'
)))

let shareTooltipTimer: ReturnType<typeof setTimeout> | null = null

const contactTagsModel = computed<TagWithFrequency[]>(
	() => (props.client.Tags ?? []) as unknown as TagWithFrequency[]
)

const onSelectorChange = async (nextTags: TagWithFrequency[]) => {
	if (isContactsReadonly.value) {
		return
	}
	const prevIds = new Set((props.client.Tags ?? []).map(t => t.Id))
	const nextIds = new Set(nextTags.map(t => t.Id))
	const added = nextTags.filter(t => !prevIds.has(t.Id))
	const removedIds = [...prevIds].filter(id => !nextIds.has(id))
	for (const tag of added) {
		await store.attachTagToContact(props.client, tag.Name)
	}
	for (const id of removedIds) {
		await store.detachTagFromContact(props.client, id)
	}
}

const onCreateTag = async (name: string) => {
	if (isContactsReadonly.value) {
		return
	}
	await store.attachTagToContact(props.client, name)
}

const onDetachTag = async (tagId: number) => {
	if (isContactsReadonly.value) {
		return
	}
	await store.detachTagFromContact(props.client, tagId)
}

const clientAddress = computed(() => {
	const addressLvl2 = props.client.AddressChain?.find(lvl => lvl.Level === 2)
	return addressLvl2 ? addressLvl2.Name : '';
})

const contactsArray = computed(() => {
	return props.client.Contacts
		? props.client.Contacts.split('\n')
		: []
})

const highlightedName = computed(() =>
	highlightSearchText(props.searchText, props.client.Name)
)

const postSegments = computed(() =>
	splitTextByPhoneNumbers(props.client.Post, props.searchText).map(el =>
		(el.isPhone || el.isEmail || el.isUrl)
			? el
			: { ...el, text: highlightSearchText(props.searchText, el.text) }
	)
)

const contactsSegments = computed(() =>
	contactsArray.value.map(item => splitTextByPhoneNumbers(item, props.searchText))
)

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const currentEditedClientId = computed(() => store.getEditedClient)
const isEditedClient = computed(() => currentEditedClientId.value === props.client.Id)

const callPhone = async (phoneNumber: string) => {
	const clearNumber = encodeURIComponent(
		phoneNumber.replace(/<span class="searchedText">|<\/span>/g,
		''
		)
	)
	await PersonApi.callPhone(clearNumber)
}

const onConfirmDelete = async () => {
	$deleteContactDropdown.value?.close()
	if (isContactsReadonly.value) {
		return
	}

	if (ctx.refetchContacts) {
		await PersonApi.deleteContact(props.client.Id)
		await ctx.refetchContacts()
	} else {
		store.deleteClient(props.client.Id)
	}
}

const onDeleteClient = async (clientId: number) => {
	if (isContactsReadonly.value) {
		return
	}
	await store.detachClientFromContact(props.client, clientId)
}

const $detachDropdowns = new Map<number, { close: () => void }>()

const setDetachDropdown = (id: number, el: any) => {
	if (el) {
		$detachDropdowns.set(id, el)
	} else {
		$detachDropdowns.delete(id)
	}
}

const closeDetachDropdown = (id: number) => {
	$detachDropdowns.get(id)?.close()
}

const onConfirmDetachClient = async (clientId: number) => {
	closeDetachDropdown(clientId)
	await onDeleteClient(clientId)
}

const onMainInProjectChange = async () => {
	if (ctx.projectId == null) {
		return
	}
	await PersonApi.setMainInProjectStatus(ctx.projectId, props.client.Id)
	if (ctx.refetchContacts) {
		await ctx.refetchContacts()
	}
}

const onCopyClientRoute = async () => {
	const targetClient = props.client.Clients?.[0]
	if (!targetClient) {
		return
	}

	const copied = await copyTextToClipboard(
		buildAbsoluteClientUrl(
			`main?page=client&id=${targetClient.Id}&contactId=${props.client.Id}`
		)
	)
	if (!copied) {
		return
	}

	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
	shareTooltipKey.value = SHARE_COPIED_KEY
	$shareTooltip.value?.showByClick()
	shareTooltipTimer = setTimeout(() => {
		shareTooltipKey.value = SHARE_TOOLTIP_KEY
		shareTooltipTimer = null
	}, SHARE_COPIED_DELAY)
}

const shareContactFromMenu = async () => {
	$contactActionsDropdown.value?.close()
	await onCopyClientRoute()
}

const openContactInfoFromMenu = () => {
	$contactActionsDropdown.value?.close()
	$clientInfoModal.value?.onOpen(props.client.Id)
}

const openContactEditFromMenu = async () => {
	$contactActionsDropdown.value?.close()
	await nextTick()
	$createEditContactModal.value?.open()
}

const openContactDeleteFromMenu = async () => {
	$contactActionsDropdown.value?.close()
	await nextTick()
	$deleteContactDropdown.value?.open()
}

onBeforeUnmount(() => {
	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
})

const onCopyPhoneAndEmail = (contact: string) => {
	copyTextToClipboard(contact
		.replace(/<span class='searchedText'>/g, '')
		.replace(/<\/span>/g, '')
	)
}

const onEmail = (email: string) => {
	const link = document.createElement('a');
	const clearEmail = email
		.replace(/<span class='searchedText'>/g, '')
		.replace(/<\/span>/g, '')
	link.href = `mailto:${clearEmail}`;
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

const openExternalLink = (url: string) => {
	const clearUrl = url
		.replace(/<span class='searchedText'>/g, '')
		.replace(/<\/span>/g, '')
	const normalizedUrl = normalizeUrlForOpening(clearUrl);

	try {
		new URL(normalizedUrl);
	} catch (error) {
		console.error('Invalid URL:', normalizedUrl);
		return;
	}

	const link = document.createElement('a');
	link.href = normalizedUrl;
	link.target = '_blank';
	link.rel = 'noopener noreferrer';
	link.setAttribute('data-original-url', url);
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

const closeAddClientDropdown = () => {
	$addClientToContact.value?.close()
}
</script>

<style scoped lang="scss">
@keyframes contactHighlightBlink {
	0%, 33.33%, 66.66%, 100% { background-color: #fff; }
	16.66%, 50%, 83.33%      { background-color: #F3F8F4; }
}

.client-item {
	display: flex;
	flex-direction: column;
	position: relative;
	width: 100%;
	padding: 12px;
	background-color: #fff;

	&--highlight {
		animation: contactHighlightBlink 1.8s ease-in-out;
	}

	&:hover {
		.client-item__footer-options-btn {
			opacity: 1;
		}
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 8px;
		font-size: 14px;
		line-height: 18px;
		margin-bottom: 4px;

		&-main {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		&-checkbox {
			align-self: flex-start;
			height: 17px;
		}

		&-name {
			font-weight: 500;
			color: #4D4D4D;
		}

		&-address {
			margin-left: auto;
			color: #979797;
			text-align: right;
		}
	}

	&__subheader {
		display: inline;
		font-size: 14px;
		line-height: 18px;
		color: #4D4D4D;
		margin-bottom: 4px;
		overflow-wrap: anywhere;
		word-break: break-word;

		&-link,
		&-text {
			display: inline;
		}

		&-link {
			font-size: 14px;
			line-height: 18px;
			color: #438451;
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	&__organizations {
		display: inline;
		margin-bottom: 4px;

		&-add {
			&-btn {
				margin-right: 2px;
				margin-top: -3px;
			}

			&-clients {
				font-size: 14px;
				line-height: 18px;
				font-weight: 500;
				color: #4D4D4D;
			}
		}

		&-list {
			display: flex;
			flex-direction: column;

			&-item {
				display: inline;

				&-link {
					font-size: 14px;
					line-height: 18px;
					color: #438451;
					text-decoration: none;

					&:hover {
						text-decoration: underline;
					}
				}

				&-delete {
					margin: -2px -5px 0 -2px;
				}

				&-tag {
					margin: -3px 0 0 0;
					color: #979797;
				}
			}
		}
	}

	&__clients {
		display: flex;
		flex-direction: column;
		margin-bottom: 4px;

		&-item {
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			overflow-wrap: anywhere;
			word-break: break-word;

			&-text {
				display: inline;
				font-size: 14px;
				line-height: 18px;
			}

			&-phone-field {
				display: inline;

				&:hover {
					.client-item__clients-item-phone-icon--called-phone {
						display: inline-flex !important;
					}

					.client-item__clients-item-phone-icon--phone {
						display: none !important;
					}
				}
			}

			&-email-field {
				display: inline;

				&:hover {
					.client-item__clients-item-email-icon {
						color: #3F884F !important;
					}
				}
			}

			&-phone {
				display: inline;
				color: #438451;
				cursor: pointer;

				&:hover {
					text-decoration: underline;
				}
				
				&-icon {
					margin-top: -2px;
					margin-left: 1px;

					&--called-phone {
						display: none !important;
					}
				}
			}

			&-email {
				display: inline;
				color: #438451;
				cursor: pointer;
				text-decoration: none;

				&:hover {
					text-decoration: underline;
				}

				&-icon {
					margin-left: 2px;
					margin-top: -3px;
				}
			}
		}
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;

		&-tags {
			display: inline;
			align-items: center;

			&-item {
				display: inline-flex;
				align-items: center;
				gap: 2px;
				margin-right: 4px;
				margin-top: 2px;
				font-size: 12px;
				line-height: 16px;

				&-name {
					color: #979797;
				}

				&-delete {
					margin-top: -1px;
				}
			}

			&-tag {
				margin-right: 2px;
			}
		}

		&-options {
			display: flex;
			align-items: flex-start;
			flex-wrap: nowrap;
			gap: 2px;
			height: 100%;
			transition: opacity 0.1s ease-in-out;

			&-btn {
				opacity: 0;
			}

			&-menu {
				margin-top: 1px;
			}
		}
	}

	&__actions-menu {
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: 8px 16px 24px 16px;
		box-sizing: border-box;

		&-item {
			display: flex;
			align-items: center;
			gap: 8px;
			min-height: 44px;
			padding: 0 8px;
			border-bottom: 1px solid #F2F2F2;
			box-sizing: border-box;
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			cursor: pointer;

			&:last-child {
				border-bottom: none;
			}
		}
	}

	&--touch {
		.client-item__footer-options-btn {
			opacity: 1;
		}
	}
}

@media (max-width: 480px) {
	.client-item__footer {
		align-items: flex-end;
	}
}
</style>
